<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="show"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
        @click="close"
        @wheel.prevent="handleWheel"
      >
        <!-- 工具栏 -->
        <div class="absolute top-4 right-4 flex items-center gap-2 z-10" @click.stop>
          <button
            @click="zoomIn"
            class="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
            title="放大"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            </svg>
          </button>
          <button
            @click="zoomOut"
            class="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
            title="缩小"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM7 10h6" />
            </svg>
          </button>
          <button
            @click="resetZoom"
            class="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
            title="重置大小"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
          <span class="px-3 py-1 bg-white/10 rounded-lg text-white text-sm min-w-[60px] text-center">
            {{ Math.round(scale * 100) }}%
          </span>
          <button
            @click="close"
            class="p-2 bg-white/10 hover:bg-red-500/80 rounded-lg text-white transition-colors ml-2"
            title="关闭 (ESC)"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- 图片容器 -->
        <div
          ref="imageContainer"
          class="relative overflow-hidden cursor-grab active:cursor-grabbing"
          :style="containerStyle"
          @click.stop
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
        >
          <img
            ref="imageEl"
            :src="src"
            :alt="alt"
            class="max-w-none select-none transition-transform duration-150"
            :style="imageStyle"
            @load="onImageLoad"
            draggable="false"
          />
        </div>

        <!-- 底部提示 -->
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
          滚轮缩放 · 拖拽移动 · ESC 关闭
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  src: {
    type: String,
    required: true
  },
  alt: {
    type: String,
    default: 'Image'
  }
})

const emit = defineEmits(['update:show', 'close'])

const imageEl = ref(null)
const imageContainer = ref(null)
const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const imageSize = ref({ width: 0, height: 0 })

const MIN_SCALE = 0.1
const MAX_SCALE = 5
const ZOOM_STEP = 0.25

const containerStyle = computed(() => ({
  width: '90vw',
  height: '90vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

const imageStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  maxWidth: '100%',
  maxHeight: '100%'
}))

const close = () => {
  emit('update:show', false)
  emit('close')
}

const zoomIn = () => {
  scale.value = Math.min(scale.value + ZOOM_STEP, MAX_SCALE)
}

const zoomOut = () => {
  scale.value = Math.max(scale.value - ZOOM_STEP, MIN_SCALE)
}

const resetZoom = () => {
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
}

const handleWheel = (e) => {
  const delta = e.deltaY > 0 ? -0.1 : 0.1
  const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale.value + delta))
  scale.value = newScale
}

const startDrag = (e) => {
  isDragging.value = true
  dragStart.value = {
    x: e.clientX - translateX.value,
    y: e.clientY - translateY.value
  }
}

const onDrag = (e) => {
  if (!isDragging.value) return
  translateX.value = e.clientX - dragStart.value.x
  translateY.value = e.clientY - dragStart.value.y
}

const endDrag = () => {
  isDragging.value = false
}

const onImageLoad = () => {
  if (imageEl.value) {
    imageSize.value = {
      width: imageEl.value.naturalWidth,
      height: imageEl.value.naturalHeight
    }
  }
}

const handleKeydown = (e) => {
  if (!props.show) return
  
  switch (e.key) {
    case 'Escape':
      close()
      break
    case '+':
    case '=':
      zoomIn()
      break
    case '-':
      zoomOut()
      break
    case '0':
      resetZoom()
      break
  }
}

watch(() => props.show, (newVal) => {
  if (newVal) {
    resetZoom()
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
